import { useState, useEffect } from 'react';
import { 
  Users, 
  Link2, 
  DollarSign, 
  TrendingUp, 
  Copy, 
  Check,
  MousePointer,
  UserPlus,
  CreditCard,
  ArrowUpRight,
  Gift,
  Layers,
  RefreshCw
} from 'lucide-react';
import api from '../lib/api';

function StatCard({ title, value, subtitle, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary-500/20 text-primary-400',
    green: 'bg-green-500/20 text-green-400',
    blue: 'bg-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/20 text-purple-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    orange: 'bg-orange-500/20 text-orange-400'
  };

  return (
    <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-dark-400 text-sm font-medium">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && (
            <p className="text-dark-500 text-xs mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function EarningRow({ earning }) {
  const levelColors = {
    1: 'text-green-400 bg-green-500/20',
    2: 'text-blue-400 bg-blue-500/20'
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-dark-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`px-2 py-1 rounded text-xs font-medium ${levelColors[earning.level]}`}>
          Level {earning.level}
        </div>
        <div>
          <p className="text-white text-sm font-medium">
            ${earning.amount.toFixed(2)} commission
          </p>
          <p className="text-dark-500 text-xs">
            {earning.tier} subscription • {earning.isRenewal ? 'Renewal' : 'New'} • {(earning.rate * 100).toFixed(0)}%
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-dark-400 text-sm">
          {new Date(earning.createdAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

function SignupRow({ signup, index }) {
  const statusColors = {
    signed_up: 'text-yellow-400 bg-yellow-500/20',
    subscribed: 'text-green-400 bg-green-500/20',
    churned: 'text-red-400 bg-red-500/20'
  };

  const tierColors = {
    free: 'text-dark-400',
    basic: 'text-blue-400',
    pro: 'text-purple-400',
    whale: 'text-yellow-400'
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-dark-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-dark-700 flex items-center justify-center">
          <Users className="w-4 h-4 text-dark-400" />
        </div>
        <div>
          <p className="text-white text-sm font-medium">{signup.displayName}</p>
          <p className="text-dark-500 text-xs">
            Signed up {new Date(signup.signedUpAt).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-sm font-medium ${tierColors[signup.tier]}`}>
          {signup.tier.charAt(0).toUpperCase() + signup.tier.slice(1)}
        </span>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[signup.status]}`}>
          {signup.status === 'signed_up' ? 'Free' : signup.status === 'subscribed' ? 'Paid' : 'Churned'}
        </span>
      </div>
    </div>
  );
}

export default function Referrals() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [signups, setSignups] = useState([]);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [statsRes, earningsRes, signupsRes] = await Promise.all([
        api.get('/referral/stats'),
        api.get('/referral/earnings?limit=20'),
        api.get('/referral/signups?limit=20')
      ]);
      
      setStats(statsRes.data);
      setEarnings(earningsRes.data.earnings || []);
      setSignups(signupsRes.data.signups || []);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copyLink = async () => {
    if (stats?.referralLink) {
      await navigator.clipboard.writeText(stats.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const commissionRates = stats?.commissionRates || { 1: 0.40, 2: 0.15 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift className="w-7 h-7 text-primary-500" />
            Referral Program
          </h1>
          <p className="text-dark-400 mt-1">
            Earn {(commissionRates[1] * 100).toFixed(0)}% on direct referrals and {(commissionRates[2] * 100).toFixed(0)}% on their referrals
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-dark-300 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Referral Link Card */}
      <div className="bg-gradient-to-r from-primary-500/20 to-purple-500/20 rounded-xl p-6 border border-primary-500/30">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary-400" />
              Your Referral Link
            </h2>
            <p className="text-dark-400 text-sm mt-1">
              Share this link to earn commissions on every subscription
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
              {(commissionRates[1] * 100).toFixed(0)}% Commission
            </span>
          </div>
        </div>
        
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 bg-dark-800 rounded-lg px-4 py-3 border border-dark-600">
            <code className="text-primary-400 text-sm break-all">
              {stats?.referralLink || 'Loading...'}
            </code>
          </div>
          <button
            onClick={copyLink}
            className="flex items-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-medium transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </button>
        </div>

        {stats?.referralCode && (
          <p className="mt-3 text-dark-500 text-sm">
            Referral Code: <span className="text-primary-400 font-mono">{stats.referralCode}</span>
          </p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Clicks"
          value={stats?.stats?.totalClicks || stats?.clickStats?.totalClicks || 0}
          subtitle="All time"
          icon={MousePointer}
          color="blue"
        />
        <StatCard
          title="Unique Clicks"
          value={stats?.stats?.uniqueClicks || stats?.clickStats?.uniqueClicks || 0}
          subtitle="Unique visitors"
          icon={MousePointer}
          color="purple"
        />
        <StatCard
          title="Total Signups"
          value={stats?.stats?.totalSignups || 0}
          subtitle={`${stats?.stats?.freeSignups || 0} free, ${stats?.stats?.paidSignups || 0} paid`}
          icon={UserPlus}
          color="green"
        />
        <StatCard
          title="Paid Subscribers"
          value={stats?.stats?.paidSignups || 0}
          subtitle="Converted to paid"
          icon={CreditCard}
          color="yellow"
        />
        <StatCard
          title="Total Earnings"
          value={`$${(stats?.earningsSummary?.totalEarnings || stats?.stats?.totalEarnings || 0).toFixed(2)}`}
          subtitle="All time"
          icon={DollarSign}
          color="green"
        />
        <StatCard
          title="Conversion Rate"
          value={`${stats?.clickStats?.conversionRate || 0}%`}
          subtitle="Clicks to signups"
          icon={TrendingUp}
          color="orange"
        />
      </div>

      {/* Commission Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Layers className="w-5 h-5 text-primary-400" />
            Commission Breakdown
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-dark-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 font-bold">L1</span>
                </div>
                <div>
                  <p className="text-white font-medium">Level 1 (Direct)</p>
                  <p className="text-dark-500 text-sm">{stats?.signupCounts?.level1 || stats?.stats?.level1Referrals || 0} referrals</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-green-400 font-bold">{(commissionRates[1] * 100).toFixed(0)}%</p>
                <p className="text-dark-500 text-sm">${(stats?.earningsSummary?.level1Earnings || 0).toFixed(2)}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-dark-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 font-bold">L2</span>
                </div>
                <div>
                  <p className="text-white font-medium">Level 2 (Indirect)</p>
                  <p className="text-dark-500 text-sm">{stats?.signupCounts?.level2 || stats?.stats?.level2Referrals || 0} referrals</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-blue-400 font-bold">{(commissionRates[2] * 100).toFixed(0)}%</p>
                <p className="text-dark-500 text-sm">${(stats?.earningsSummary?.level2Earnings || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          
          {stats?.earningsSummary?.renewalEarnings > 0 && (
            <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
              <div className="flex items-center justify-between">
                <span className="text-purple-400 text-sm">Recurring Earnings</span>
                <span className="text-purple-400 font-bold">${stats.earningsSummary.renewalEarnings.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-dark-800 rounded-xl p-5 border border-dark-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <ArrowUpRight className="w-5 h-5 text-primary-400" />
            How It Works
          </h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold text-sm">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Share Your Link</p>
                <p className="text-dark-500 text-sm">Send your unique referral link to friends and followers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold text-sm">2</span>
              </div>
              <div>
                <p className="text-white font-medium">They Subscribe</p>
                <p className="text-dark-500 text-sm">When they sign up and subscribe to a paid plan</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-primary-400 font-bold text-sm">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Earn Commission</p>
                <p className="text-dark-500 text-sm">Get {(commissionRates[1] * 100).toFixed(0)}% on their payment + {(commissionRates[2] * 100).toFixed(0)}% on their referrals</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-400 font-bold text-sm">∞</span>
              </div>
              <div>
                <p className="text-white font-medium">Recurring Revenue</p>
                <p className="text-dark-500 text-sm">Earn on every renewal, not just the first payment</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-dark-700">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-white'
            }`}
          >
            Recent Earnings
          </button>
          <button
            onClick={() => setActiveTab('signups')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'signups'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-white'
            }`}
          >
            Referral Signups
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-dark-800 rounded-xl border border-dark-700">
        {activeTab === 'overview' && (
          <div className="p-5">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Earnings</h3>
            {earnings.length > 0 ? (
              <div className="divide-y divide-dark-700">
                {earnings.map((earning, index) => (
                  <EarningRow key={earning.id || index} earning={earning} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-dark-600 mx-auto mb-3" />
                <p className="text-dark-400">No earnings yet</p>
                <p className="text-dark-500 text-sm mt-1">Share your referral link to start earning</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'signups' && (
          <div className="p-5">
            <h3 className="text-lg font-semibold text-white mb-4">Your Referrals</h3>
            {signups.length > 0 ? (
              <div className="divide-y divide-dark-700">
                {signups.map((signup, index) => (
                  <SignupRow key={signup.id || index} signup={signup} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-dark-600 mx-auto mb-3" />
                <p className="text-dark-400">No referrals yet</p>
                <p className="text-dark-500 text-sm mt-1">Share your link to get started</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
